import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import EmailComposer from '../components/EmailComposer';
import LabelManager from '../components/LabelManager';
import RuleManager from '../components/RuleManager';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = { primary: '#2b3380', success: '#4caf50', warning: '#ff9800', error: '#f44336' };

function EmailInbox({ user, onLogout }) {
  const [emails, setEmails] = useState([]);
  const [folder, setFolder] = useState('inbox');
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [replyToEmail, setReplyToEmail] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [labels, setLabels] = useState([]);
  const [showLabelManager, setShowLabelManager] = useState(false);
  const [showRuleManager, setShowRuleManager] = useState(false);
  const [showLabelDropdown, setShowLabelDropdown] = useState(null);
  const [senders, setSenders] = useState([]);
  const [selectedSender, setSelectedSender] = useState(null);
  const [showSenders, setShowSenders] = useState(false);

  useEffect(() => {
    fetchEmails();
    fetchStats();
    fetchLabels();
    fetchSenders();
  }, [folder, selectedSender]);

  const fetchEmails = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/api/emails?folder=${folder}&search=${searchQuery}`;
      
      // Add sender filter if selected
      if (selectedSender) {
        url = `${API}/api/emails?folder=${folder}&search=${selectedSender}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmails(response.data.emails || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching emails:', error);
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/emails/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchLabels = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/emails/labels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLabels(response.data.labels || []);
    } catch (error) {
      console.error('Error fetching labels:', error);
    }
  };

  const fetchSenders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/emails/senders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSenders(response.data.senders || []);
    } catch (error) {
      console.error('Error fetching senders:', error);
    }
  };

  const handleApplyLabels = async (emailId, selectedLabels) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/emails/${emailId}/labels`, 
        { labels: selectedLabels },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchEmails();
      setShowLabelDropdown(null);
    } catch (error) {
      console.error('Error applying labels:', error);
      alert('Error applying labels');
    }
  };

  const handleStarToggle = async (emailId, isStarred) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/emails/${emailId}/star?is_starred=${!isStarred}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEmails();
      fetchStats();
    } catch (error) {
      console.error('Error toggling star:', error);
    }
  };

  const handleDelete = async (emailId) => {
    if (window.confirm('Move to trash?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/api/emails/${emailId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchEmails();
        fetchStats();
      } catch (error) {
        console.error('Error deleting email:', error);
      }
    }
  };

  const folders = [
    { name: 'Inbox', value: 'inbox', icon: '📥', count: stats.inbox },
    { name: 'Starred', value: 'starred', icon: '⭐', count: stats.starred },
    { name: 'Sent', value: 'sent', icon: '📤', count: stats.sent },
    { name: 'Drafts', value: 'drafts', icon: '📝', count: stats.drafts },
    { name: 'Trash', value: 'trash', icon: '🗑️', count: stats.trash },
    { name: 'Spam', value: 'spam', icon: '⚠️', count: stats.spam },
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="h-screen flex flex-col bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-md p-4 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: COLORS.primary }}>📧 Email</h1>
            {selectedSender && (
              <div className="mt-1 flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  Showing emails from: <span className="font-semibold">{selectedSender}</span>
                </span>
                <button
                  onClick={() => setSelectedSender(null)}
                  className="text-xs bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
                >
                  Clear Filter
                </button>
              </div>
            )}
          </div>
          <button
            onClick={() => { setShowCompose(true); setReplyToEmail(null); }}
            className="px-6 py-2 text-white rounded-lg font-medium hover:opacity-90"
            style={{ backgroundColor: COLORS.primary }}
          >
            ✍️ Compose
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
            <div className="p-4">
              {folders.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setFolder(f.value)}
                  className={`w-full text-left px-4 py-3 rounded-lg mb-2 flex items-center justify-between hover:bg-gray-100 transition ${
                    folder === f.value ? 'bg-blue-50' : ''
                  }`}
                  style={folder === f.value ? { backgroundColor: '#e3f2fd' } : {}}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-xl">{f.icon}</span>
                    <span className="font-medium text-gray-700">{f.name}</span>
                  </span>
                  {f.count > 0 && (
                    <span className="bg-gray-200 text-gray-700 text-xs font-bold px-2 py-1 rounded-full">
                      {f.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Senders Section */}
            <div className="mt-4 border-t border-gray-200 pt-4">
              <button
                onClick={() => setShowSenders(!showSenders)}
                className="w-full text-left px-4 py-2 font-semibold text-gray-700 hover:bg-gray-100 rounded-lg transition flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <span className="text-xl">👤</span>
                  <span>Senders ({senders.length})</span>
                </span>
                <span className="text-sm">{showSenders ? '▼' : '▶'}</span>
              </button>
              
              {showSenders && (
                <div className="mt-2 max-h-64 overflow-y-auto">
                  {senders.length === 0 ? (
                    <p className="text-xs text-gray-500 px-4 py-2">No senders yet</p>
                  ) : (
                    <div className="space-y-1">
                      {senders.map((sender) => (
                        <button
                          key={sender.email}
                          onClick={() => {
                            setSelectedSender(sender.email === selectedSender ? null : sender.email);
                            setFolder('sent'); // Switch to sent folder when selecting sender
                          }}
                          className={`w-full text-left px-4 py-2 rounded-lg text-sm hover:bg-gray-100 transition ${
                            selectedSender === sender.email ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-700 truncate flex-1">
                              {sender.email.split('@')[0]}
                            </span>
                            <span className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full ml-2">
                              {sender.count}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {sender.email}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Storage */}
            <div className="p-4 border-t border-gray-200">
              <Link to="/drive" className="block mb-2">
                <button className="w-full px-4 py-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition">
                  <span className="text-xl">💾</span>
                  <span className="ml-2 font-medium text-gray-700">Drive</span>
                </button>
              </Link>
              
              {/* Label Management */}
              <button
                onClick={() => setShowLabelManager(true)}
                className="w-full px-4 py-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition mb-2"
              >
                <span className="text-xl">🏷️</span>
                <span className="ml-2 font-medium text-gray-700">Manage Labels</span>
              </button>
              
              {/* Rule Management */}
              <button
                onClick={() => setShowRuleManager(true)}
                className="w-full px-4 py-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition"
              >
                <span className="text-xl">⚙️</span>
                <span className="ml-2 font-medium text-gray-700">Auto-Rules</span>
              </button>
            </div>
          </div>

          {/* Email List */}
          <div className="flex-1 bg-white overflow-y-auto">
            {/* Search Bar */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4">
              <input
                type="text"
                placeholder="Search emails..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchEmails()}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                style={{ focusRingColor: COLORS.primary }}
              />
            </div>

            {/* Email Items */}
            {loading ? (
              <div className="text-center py-20">
                <div className="text-4xl mb-4">⏳</div>
                <p className="text-gray-600">Loading emails...</p>
              </div>
            ) : emails.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-6xl mb-4">📭</div>
                <p className="text-xl text-gray-600">No emails in {folder}</p>
              </div>
            ) : (
              <div>
                {emails.map((email) => (
                  <div
                    key={email.id}
                    className={`border-b border-gray-200 p-4 hover:bg-gray-50 cursor-pointer ${
                      !email.is_read ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => setSelectedEmail(email)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStarToggle(email.id, email.is_starred);
                            }}
                            className="text-xl hover:scale-125 transition"
                          >
                            {email.is_starred ? '⭐' : '☆'}
                          </button>
                          <span className={`font-semibold ${
                            !email.is_read ? 'text-gray-900' : 'text-gray-600'
                          }`}>
                            {folder === 'sent' ? email.to.join(', ') : email.user_email}
                          </span>
                        </div>
                        <p className={`font-medium mb-1 ${
                          !email.is_read ? 'text-gray-900' : 'text-gray-700'
                        }`}>
                          {email.subject}
                        </p>
                        
                        {/* Label Badges */}
                        {email.labels && email.labels.length > 0 && (
                          <div className="flex gap-1 mb-1 flex-wrap">
                            {email.labels.map((labelName) => {
                              const label = labels.find(l => l.name === labelName);
                              return label ? (
                                <span
                                  key={labelName}
                                  className="px-2 py-0.5 text-xs rounded-full text-white font-medium"
                                  style={{ backgroundColor: label.color }}
                                >
                                  {labelName}
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                        
                        <p className="text-sm text-gray-600 line-clamp-1">
                          {email.body.replace(/<[^>]*>/g, '')}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className="text-xs text-gray-500">
                          {new Date(email.sent_at || email.created_at).toLocaleDateString()}
                        </span>
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowLabelDropdown(showLabelDropdown === email.id ? null : email.id);
                            }}
                            className="text-gray-400 hover:text-blue-500 text-lg"
                            title="Apply labels"
                          >
                            🏷️
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(email.id);
                            }}
                            className="text-gray-400 hover:text-red-500"
                          >
                            🗑️
                          </button>
                        </div>
                        
                        {/* Label Dropdown */}
                        {showLabelDropdown === email.id && (
                          <div className="absolute mt-8 right-4 bg-white shadow-lg rounded-lg border border-gray-200 p-3 z-10 w-48"
                               onClick={(e) => e.stopPropagation()}>
                            <div className="text-sm font-semibold mb-2">Apply Labels</div>
                            {labels.length === 0 ? (
                              <p className="text-xs text-gray-500">No labels. Create one!</p>
                            ) : (
                              <div className="space-y-1">
                                {labels.map((label) => {
                                  const isChecked = email.labels?.includes(label.name);
                                  return (
                                    <label key={label.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => {
                                          const newLabels = e.target.checked
                                            ? [...(email.labels || []), label.name]
                                            : (email.labels || []).filter(l => l !== label.name);
                                          handleApplyLabels(email.id, newLabels);
                                        }}
                                        className="rounded"
                                      />
                                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: label.color }} />
                                      <span className="text-sm">{label.name}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Compose Modal */}
      {showCompose && (
        <EmailComposer
          onClose={() => { setShowCompose(false); setReplyToEmail(null); }}
          onSent={() => { fetchEmails(); fetchStats(); }}
          replyTo={replyToEmail}
        />
      )}

      {/* Email View Modal */}
      {selectedEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold" style={{ color: COLORS.primary }}>{selectedEmail.subject}</h2>
              <button onClick={() => setSelectedEmail(null)} className="text-2xl text-gray-500 hover:text-gray-700">×</button>
            </div>
            <div className="p-6">
              <div className="mb-4 pb-4 border-b border-gray-200">
                <p className="text-sm text-gray-600">From: {selectedEmail.user_email}</p>
                <p className="text-sm text-gray-600">To: {selectedEmail.to.join(', ')}</p>
                {selectedEmail.cc && selectedEmail.cc.length > 0 && (
                  <p className="text-sm text-gray-600">CC: {selectedEmail.cc.join(', ')}</p>
                )}
                <p className="text-sm text-gray-600 mt-2">
                  {new Date(selectedEmail.sent_at || selectedEmail.created_at).toLocaleString()}
                </p>
                
                {/* Labels in Email View */}
                {selectedEmail.labels && selectedEmail.labels.length > 0 && (
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {selectedEmail.labels.map((labelName) => {
                      const label = labels.find(l => l.name === labelName);
                      return label ? (
                        <span
                          key={labelName}
                          className="px-2 py-1 text-xs rounded-full text-white font-medium"
                          style={{ backgroundColor: label.color }}
                        >
                          {labelName}
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
                
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => {
                      setReplyToEmail(selectedEmail);
                      setShowCompose(true);
                      setSelectedEmail(null);
                    }}
                    className="px-4 py-2 text-white rounded-lg font-medium hover:opacity-90"
                    style={{ backgroundColor: COLORS.primary }}
                  >
                    ↩️ Reply
                  </button>
                  <button
                    onClick={() => {
                      handleStarToggle(selectedEmail.id, selectedEmail.is_starred);
                      setSelectedEmail({ ...selectedEmail, is_starred: !selectedEmail.is_starred });
                    }}
                    className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                  >
                    {selectedEmail.is_starred ? '⭐ Starred' : '☆ Star'}
                  </button>
                  <button
                    onClick={() => {
                      handleDelete(selectedEmail.id);
                      setSelectedEmail(null);
                    }}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
              <div className="pt-4">
                <div dangerouslySetInnerHTML={{ __html: selectedEmail.body }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Label Manager Modal */}
      {showLabelManager && (
        <LabelManager
          user={user}
          onClose={() => setShowLabelManager(false)}
          onLabelsUpdated={() => { fetchLabels(); fetchEmails(); }}
        />
      )}

      {/* Rule Manager Modal */}
      {showRuleManager && (
        <RuleManager
          user={user}
          onClose={() => setShowRuleManager(false)}
        />
      )}
    </Layout>
  );
}

export default EmailInbox;
